import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Sparkles, Loader2, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import {
  CURRENCY_GROUPS,
  CURRENCY_LABELS,
  detectCurrencyFromGeo,
  formatPrice,
  getPlanPriceDisplay,
} from '../data/pricingCurrencies';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 28 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

/** Soft launch: Free signup open; paid quarterly plans locked until next week. Flip to false to restore payments. */
const SOFT_LAUNCH_MODE = true;

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    badge: 'Starter',
    free: true,
    audience: 'For small teams & HR trials',
    employees: 'Up to 10 Employees',
    features: [
      'Up to 10 active employee responses',
      'Standard Anonymous Pulse Surveys',
      'Basic Burnout Risk Score',
      '1 Admin / HR Dashboard seat',
      'Strict Privacy Protection',
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    employees: '40 – 100 Employees',
    features: [
      'Q1 Pulse Survey launch',
      'Team-level analytics dashboard',
      'Anonymous reminder emails',
      'PDF executive summary',
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    employees: '101 – 200 Employees',
    popular: true,
    features: [
      'Everything in Starter',
      'Predictive burnout alerts',
      'Department health heatmap',
      'Compare quarterly reviews',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    employees: '350+ Employees',
    enterprise: true,
    features: [
      'Unlimited departments',
      'Dedicated success manager',
      'SSO & advanced security',
      'Custom integrations & SLA',
    ],
  },
];

const columnVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.12 + i * 0.1, duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Pricing({ inApp = false }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [activating, setActivating] = useState(null);
  const [currency, setCurrency] = useState('AED');
  const [geoLoading, setGeoLoading] = useState(true);
  const manualOverride = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const detected = await detectCurrencyFromGeo();
      if (!cancelled && !manualOverride.current) {
        setCurrency(detected);
      }
      if (!cancelled) setGeoLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCurrencyChange = (code) => {
    manualOverride.current = true;
    setCurrency(code);
  };

  const handleSelect = async (plan) => {
    if (plan.free) {
      if (user && user.email) {
        navigate('/dashboard');
        return;
      }
      navigate('/register');
      return;
    }
    if (plan.enterprise) {
      navigate('/contact');
      return;
    }
    // Soft launch: block paid quarterly activation
    if (SOFT_LAUNCH_MODE) return;
    if (!user || !user.email) {
      navigate('/login?signup=1');
      return;
    }
    setActivating(plan.id);
    try {
      await axios.post(`${API}/auth/activate-plan`, { plan: plan.id }, { withCredentials: true });
      if (refreshUser) await refreshUser();
      navigate('/dashboard');
    } catch {
      setActivating(null);
    }
  };

  return (
    <div
      className={`min-h-screen ${inApp ? '' : 'pt-20'}`}
      style={{ backgroundColor: '#080C14' }}
      data-testid="pricing-page"
    >
      {/* Hero + panel share one dark canvas */}
      <section className="relative overflow-hidden pb-20 md:pb-28">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 70% 50% at 50% -10%, rgba(13,148,136,0.35) 0%, transparent 55%), radial-gradient(ellipse 40% 40% at 85% 40%, rgba(34,211,238,0.12) 0%, transparent 50%), radial-gradient(ellipse 35% 35% at 10% 60%, rgba(20,184,166,0.14) 0%, transparent 50%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.22] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(rgba(148,163,184,0.35) 0.7px, transparent 0.7px)',
            backgroundSize: '22px 22px',
            maskImage: 'radial-gradient(ellipse at 50% 30%, black 15%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse at 50% 30%, black 15%, transparent 70%)',
          }}
        />

        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-10 relative z-10 pt-14 md:pt-20">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="text-center mb-10 md:mb-14">
            <motion.div
              variants={fadeUp}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-500/15 border border-teal-400/30 mb-6 shadow-[0_0_24px_rgba(45,212,191,0.15)]"
            >
              <Sparkles className="w-3.5 h-3.5 text-teal-300" />
              <span className="text-xs font-medium text-teal-300 tracking-wide">
                Quarterly Upfront Billing · Global Pricing
              </span>
            </motion.div>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-4xl md:text-5xl font-semibold text-white mb-4 tracking-tight"
            >
              {inApp ? 'Upgrade your plan' : 'Simple, Transparent Pricing'}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
              {inApp
                ? 'You are already signed in. Stay on Free or upgrade when your team grows — paid plans unlock next week.'
                : 'Start free, then unlock full HR analytics with a single quarterly payment in your local currency — pulse surveys, burnout intelligence, and retention tools included.'}
            </motion.p>
          </motion.div>

          {/* Currency switcher */}
          <div
            className="flex flex-col sm:flex-row sm:justify-end sm:items-center gap-2 mb-6"
            data-testid="currency-switcher"
          >
            <div className="flex items-center gap-2 sm:ml-auto">
              <Globe className="w-4 h-4 text-teal-400/80 shrink-0" />
              <label htmlFor="currency-select" className="text-xs text-slate-400 whitespace-nowrap">
                Display currency
              </label>
              {geoLoading && (
                <Loader2 className="w-4 h-4 animate-spin text-teal-400" aria-label="Detecting location" />
              )}
            </div>
            <select
              id="currency-select"
              value={currency}
              onChange={(e) => handleCurrencyChange(e.target.value)}
              className="text-sm font-medium text-slate-200 bg-slate-900/70 border border-white/10 rounded-xl px-3 py-2.5 backdrop-blur-md shadow-[0_0_20px_rgba(13,148,136,0.08)] hover:border-teal-400/40 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 cursor-pointer max-w-xs sm:max-w-sm"
              data-testid="currency-select"
            >
              {CURRENCY_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label} className="bg-slate-900 text-slate-200">
                  {group.codes.map((code) => (
                    <option key={code} value={code} className="bg-slate-900">
                      {CURRENCY_LABELS[code]}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Unified glassmorphic pricing panel */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[1.75rem] border border-white/10 overflow-hidden shadow-[0_0_60px_-12px_rgba(13,148,136,0.45),0_0_80px_-20px_rgba(34,211,238,0.25)]"
            style={{
              background:
                'linear-gradient(160deg, rgba(255,255,255,0.08) 0%, rgba(15,23,42,0.72) 42%, rgba(8,12,20,0.85) 100%)',
              backdropFilter: 'blur(28px)',
              WebkitBackdropFilter: 'blur(28px)',
            }}
            data-testid="pricing-panel"
          >
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-teal-300/50 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-cyan-400/25 to-transparent" />
            <div
              className="absolute -top-24 left-1/2 -translate-x-1/2 w-[70%] h-40 pointer-events-none opacity-60"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(45,212,191,0.22) 0%, transparent 70%)',
              }}
            />

            <div className="relative grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
              {PLANS.map((plan, index) => {
                const price = plan.enterprise || plan.free ? null : getPlanPriceDisplay(plan.id, currency);
                const freePrice = plan.free ? formatPrice(0, currency) : null;
                const isLast = index === PLANS.length - 1;

                return (
                  <motion.div
                    key={plan.id}
                    custom={index}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-20px' }}
                    variants={columnVariants}
                    className={`relative flex flex-col min-w-0 h-full px-6 py-8 sm:px-7 sm:py-9 ${
                      !isLast
                        ? 'md:border-r md:border-white/10'
                        : ''
                    } ${
                      plan.popular
                        ? 'bg-gradient-to-b from-teal-500/15 via-teal-500/[0.06] to-transparent shadow-[inset_0_0_40px_rgba(13,148,136,0.12)]'
                        : ''
                    }`}
                    data-testid={`plan-${plan.id}`}
                    data-currency={currency}
                  >
                    {/* Vertical neon edge for popular */}
                    {plan.popular && (
                      <div className="absolute inset-y-6 left-0 w-px bg-gradient-to-b from-transparent via-teal-300/70 to-transparent hidden xl:block shadow-[0_0_12px_rgba(45,212,191,0.8)]" />
                    )}

                    <div className="mb-5 min-h-[4.5rem]">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-300">
                          {plan.name}
                        </p>
                        {plan.badge && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-white/10 text-slate-200 border border-white/10">
                            {plan.badge}
                          </span>
                        )}
                        {plan.popular && (
                          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-teal-500 text-white shadow-[0_0_18px_rgba(20,184,166,0.55)]">
                            Most Popular
                          </span>
                        )}
                        {SOFT_LAUNCH_MODE && !plan.free && !plan.enterprise && (
                          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold tracking-wide bg-amber-500/15 text-amber-200/90 border border-amber-400/25">
                            Available Next Week
                          </span>
                        )}
                      </div>
                      {plan.audience && (
                        <p className="text-sm text-slate-400 leading-snug break-words">{plan.audience}</p>
                      )}
                      <p className={`text-sm leading-snug break-words ${plan.audience ? 'text-slate-300 mt-1 font-medium' : 'text-slate-400'}`}>
                        {plan.employees}
                      </p>
                    </div>

                    <div className="mb-7 min-h-[4.75rem]" data-testid={`plan-price-${plan.id}`}>
                      {plan.enterprise ? (
                        <>
                          <p className="font-heading text-2xl sm:text-[1.65rem] font-semibold text-white leading-tight break-words">
                            Get Quote
                          </p>
                          <p className="text-sm text-slate-400 mt-1.5">Talk to Sales</p>
                        </>
                      ) : plan.free ? (
                        <>
                          <p className="font-heading text-2xl sm:text-[1.65rem] font-semibold text-white leading-tight break-words">
                            {freePrice}
                          </p>
                          <p className="text-sm text-slate-400 mt-1.5">/ month</p>
                        </>
                      ) : (
                        <>
                          <p className="font-heading text-2xl sm:text-[1.65rem] font-semibold text-white leading-tight break-words">
                            {price}
                          </p>
                          <p className="text-sm text-slate-400 mt-1.5 leading-snug">
                            / Quarter (One-Time Payment)
                          </p>
                        </>
                      )}
                    </div>

                    <ul className="space-y-4 mb-8 flex-1 min-w-0">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-start gap-3 min-w-0">
                          <span className="mt-0.5 w-5 h-5 rounded-full bg-teal-500/15 border border-teal-400/30 flex items-center justify-center shrink-0 shadow-[0_0_10px_rgba(45,212,191,0.25)]">
                            <Check className="w-3 h-3 text-teal-300" />
                          </span>
                          <span className="text-sm text-slate-300 leading-relaxed break-words min-w-0">
                            {f}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {plan.free ? (
                      inApp ? (
                        <Button
                          disabled
                          className="w-full h-11 shrink-0 whitespace-nowrap bg-teal-500/15 text-teal-200 border border-teal-400/40"
                          data-testid="select-plan-free"
                        >
                          Current plan
                        </Button>
                      ) : (
                        <Button
                          asChild
                          className="w-full h-11 shrink-0 whitespace-nowrap bg-transparent border border-teal-400/50 text-teal-200 hover:bg-teal-500/10 hover:border-teal-300/70"
                          data-testid="select-plan-free"
                        >
                          <Link to="/register">
                            Start Free <ArrowRight className="w-4 h-4 ml-2 shrink-0" />
                          </Link>
                        </Button>
                      )
                    ) : (
                      <Button
                        onClick={() => handleSelect(plan)}
                        disabled={
                          activating === plan.id ||
                          (SOFT_LAUNCH_MODE && !plan.enterprise)
                        }
                        className={`w-full h-11 shrink-0 whitespace-nowrap ${
                          SOFT_LAUNCH_MODE && !plan.enterprise
                            ? 'bg-slate-800/80 text-slate-500 border border-white/5 cursor-not-allowed opacity-70 pointer-events-none shadow-none'
                            : plan.popular
                            ? 'bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold shadow-[0_0_28px_rgba(20,184,166,0.55)] hover:shadow-[0_0_36px_rgba(34,211,238,0.45)]'
                            : plan.enterprise
                            ? 'bg-white/10 hover:bg-white/15 text-white border border-white/15'
                            : 'bg-transparent border border-teal-400/50 text-teal-200 hover:bg-teal-500/10 hover:border-teal-300/70'
                        }`}
                        data-testid={`select-plan-${plan.id}`}
                      >
                        {activating === plan.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : plan.enterprise ? (
                          <>
                            Talk to Sales <ArrowRight className="w-4 h-4 ml-2 shrink-0" />
                          </>
                        ) : SOFT_LAUNCH_MODE ? (
                          <>Unlocks Soon</>
                        ) : (
                          <>Activate Quarterly Plan</>
                        )}
                      </Button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          <p className="text-center text-xs text-slate-500 mt-10 max-w-2xl mx-auto leading-relaxed px-2">
            {SOFT_LAUNCH_MODE
              ? 'Soft launch: the Free plan is open now — no credit card required. Starter and Growth quarterly plans unlock next week. Enterprise quotes remain available via sales.'
              : `Free plan requires no credit card. Paid plans are billed as a single upfront quarterly payment in ${currency}. Prices auto-detect your region via IP geolocation; use the currency selector to override. Unlisted countries default to USD. Dashboard unlocks immediately upon activation.`}
          </p>
        </div>
      </section>

      {/* FAQ strip */}
      <section className="py-12 border-t border-white/5 bg-[#060910]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-sm text-slate-400">
            Questions about team size or enterprise needs?{' '}
            <Link to="/contact" className="text-teal-400 font-medium hover:underline">
              Contact our sales team
            </Link>{' '}
            or read our{' '}
            <Link to="/faq" className="text-teal-400 font-medium hover:underline">
              FAQ
            </Link>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
